import { useCallback, useRef } from "react";
import { electronModule } from "@/modules/electron";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { useAppStore } from "@/store";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { RecallEventWindow, RecallMeetingDetectedEventKind } from "@/modules/electron/types";
import { EventContext } from "@/domains/metrics/context";
import { SystemPermissionsModal } from "@/store/system-permissions";
import { DateTime } from "luxon";
import { windowModule } from "@/modules/window";
import { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";
import { createNoteEncodedContentForAutoStartVoice } from "@/store/note/helpers/auto-start-voice";
import { PreparedRecording } from "@/store/audio/recording/prepared-recording-controller";
import { TrashRecordingReason } from "@/store/audio/audio.types";
import { CreateNoteSourceOperation } from "@/store/sync/operations/sources/CreateNoteSourceOperation";
import { resolveNoteSourceSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteSourceSyncModelUuid";
import { resolveSourceIdForCalendarEvent } from "@/modules/uuid/sync-models/resolveSourceIdForCalendarEvent";

export const useSystemAudioPermissionSynchronizer = (): void => {
  const { store } = useAppStore();

  const syncPermissions = useCallback(async () => {
    try {
      const [
        permissionMap,
        { enabled: teamsMeetingDetectionEnabled },
        { enabled: meetingNotificationEnabled },
        { enabled: calendarNotificationEnabled },
      ] = await Promise.all([
        electronModule.recall.getPermissionStatus(),
        electronModule.getTeamsMeetingDetectionEnabled(),
        electronModule.getMeetingNotificationEnabled(),
        electronModule.getCalendarNotificationEnabled(),
      ]);

      logger.info({
        message: "[SystemAudioPermissionSynchronizer] Syncing",
        info: { teamsMeetingDetectionEnabled, permissionMap },
      });

      store.systemPermissions.setPermissionMap({ permissionMap });
      store.systemPermissions.setTeamsMeetingDetectionEnabled({
        enabled: teamsMeetingDetectionEnabled,
      });
      store.systemPermissions.setMeetingNotificationEnabled({
        enabled: meetingNotificationEnabled,
      });
      store.systemPermissions.setCalendarNotificationEnabled({
        enabled: calendarNotificationEnabled,
      });

      const { ui } = store.systemPermissions;

      if (!ui.activeModal) {
        return;
      }

      if (ui.activeModal === SystemPermissionsModal.Voice) {
        if (store.systemPermissions.hasRequiredVoicePermissions) {
          ui.closeModal();
        }
      }

      if (ui.activeModal === SystemPermissionsModal.Meeting) {
        if (store.systemPermissions.hasRequiredMeetingPermissions) {
          ui.closeModal();
        }
      }

      if (ui.activeModal === SystemPermissionsModal.AdvancedMeeting) {
        if (store.systemPermissions.hasRequiredAdvancedMeetingPermissions) {
          ui.closeModal();
        }
      }
    } catch (error) {
      logger.error({
        message: "[SystemAudioPermissionSynchronizer] Failed",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    }
  }, [store.systemPermissions]);

  /**
   * Initialize permission state and subscribe to changes.
   */
  useEffectOnMount(() => {
    if (!store.interface.isDesktop) {
      return;
    }

    if (store.electron.isIntelMacDesktop) {
      /**
       * This feature isn't currently supported on Intel-based MacOS devices.
       */
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const asyncLogic = async () => {
      await syncPermissions();

      try {
        unsubscribe = await electronModule.recall.onPermissionStatus?.({
          callback: () => {
            void syncPermissions();
          },
        });
      } catch (_) {
        /* empty */
      }
    };

    void asyncLogic();

    return () => {
      try {
        unsubscribe?.();
      } catch (_) {
        /* empty */
      }
    };
  });
};

/**
 * Listens to meeting detection events and handles them by either joining an ongoing recording
 * or creating a new note with the detected meeting window.
 */
export const useMeetingDetectionListener = (): void => {
  const { store } = useAppStore();
  const advancedMeetingPermissionShownRef = useRef(false);
  const preparedRecordingsRef = useRef(new Map<string, PreparedRecording>());
  const pendingPrewarmRef = useRef<{ audioRecordingId: string; meetingWindowId: string } | null>(
    null
  );

  const clearPendingPrewarm = useCallback(() => {
    const pending = pendingPrewarmRef.current;
    if (pending) {
      preparedRecordingsRef.current.delete(pending.audioRecordingId);
      pendingPrewarmRef.current = null;
    }
  }, []);

  const handleMeetingDetected = useCallback(
    async ({
      event,
      kind,
      debug,
    }: {
      event: {
        window: RecallEventWindow;
        info?: {
          calendarEventId?: string | null;
          audioRecordingId?: string;
          startTime?: string | null;
          endTime?: string | null;
          attendees?: string[] | null;
        };
      };
      kind: RecallMeetingDetectedEventKind;
      debug: boolean;
    }) => {
      const { window, info } = event;

      const hasFocus = document.hasFocus();
      logger.info({
        message: `[${kind}] Meeting event received (${window.id})`,
        info: {
          debug,
          window,
          info: info ?? null,
          kind,
          visibilityState: document.visibilityState,
          hidden: document.hidden,
          hasFocus,
        },
      });

      if (debug) {
        store.electron.pushRecallDebugEvent(window, kind);
        if (kind === RecallMeetingDetectedEventKind.MeetingDetected) {
          if (
            event.window.platform === "teams" &&
            !store.systemPermissions.hasRequiredAdvancedMeetingPermissions &&
            !advancedMeetingPermissionShownRef.current
          ) {
            store.systemPermissions.ui.openAdvancedMeetingPermissionsModal();
            electronModule.setAdvancedMeetingDetectionShown({ shown: true });
            advancedMeetingPermissionShownRef.current = true;
            return;
          }

          // Return early if it's not idle.
          if (store.audio.activeRecordingId || store.audio.recording.isInitializing) {
            return;
          }

          // Clear any stale prewarm from a prior notification that was
          // dismissed or auto-hidden without starting a recording.
          clearPendingPrewarm();

          const preparedRecording = store.audio.getPreparedRecording();
          if (preparedRecording) {
            // Store prepared recording here to be restored below.
            // When the user clicks the notification, the renderer drives the start
            // using this cached PreparedRecording.
            preparedRecordingsRef.current.set(
              preparedRecording.audioRecordingId,
              preparedRecording
            );
            pendingPrewarmRef.current = {
              audioRecordingId: preparedRecording.audioRecordingId,
              meetingWindowId: window.id,
            };
          }
        }
        return;
      }

      if (kind === RecallMeetingDetectedEventKind.MeetingClosed) {
        clearPendingPrewarm();
        const activeAudioRecordingId = store.audio.activeRecordingId;

        const associatedNoteId = store.audio.activeRecordingNoteId;

        if (document.visibilityState === "hidden" || document.hidden) {
          logger.info({
            message:
              "[MeetingDetectionListener] Ignoring meeting closed event because page is hidden",
            info: {
              visibilityState: document.visibilityState,
              hidden: document.hidden,
              hasFocus,
              activeAudioRecordingId,
              associatedNoteId: associatedNoteId ?? null,
            },
          });
          return;
        }

        if (associatedNoteId) {
          store.navigation.goToNote({ noteId: associatedNoteId });
        }

        if (!activeAudioRecordingId) return;

        store.audio.finishRecording({
          audioRecordingId: activeAudioRecordingId,
        });
        return;
      }

      const preparedRecording = (() => {
        const tooSoonToJoinMeeting =
          // Detected meeting.
          !!info?.startTime &&
          // Calendar meeting is close so we join for the user.
          DateTime.fromISO(info.startTime).diffNow().as("minutes") > 1;

        if (tooSoonToJoinMeeting) return;

        if (!info?.audioRecordingId) {
          // Only reuse the prewarmed recording if this event is for the same
          // meeting window that triggered the prewarm. This prevents a calendar
          // notification start from accidentally inheriting a stale prewarm
          // from an earlier detected meeting.
          const pending = pendingPrewarmRef.current;
          if (pending && pending.meetingWindowId === window.id) {
            pendingPrewarmRef.current = null;
            const cached = preparedRecordingsRef.current.get(pending.audioRecordingId);
            if (cached) {
              preparedRecordingsRef.current.delete(pending.audioRecordingId);
              return cached;
            }
          }
          return store.audio.recording.getPreparedRecording();
        }

        const preparedRecording = preparedRecordingsRef.current.get(info.audioRecordingId);
        if (preparedRecording) {
          preparedRecordingsRef.current.delete(info.audioRecordingId);
          return preparedRecording;
        }

        return store.audio.recording.getPreparedRecording(info.audioRecordingId);
      })();

      const startRecording = (noteId: Uuid) => {
        logger.info({
          message: "[MeetingDetectionListener] Auto-starting voice recording?",
          info: {
            noteId,
            window,
            info: info ?? null,
            calendarEventId: info?.calendarEventId ?? null,
            audioRecordingId: preparedRecording?.audioRecordingId ?? null,
            autoStartVoice: !!preparedRecording,
          },
        });

        if (window?.url) {
          void windowModule.openInNewTab({ url: new URL(window.url) });
        }

        if (!preparedRecording) return;

        store.electron.pushRecallDebugEvent(window, "auto-start-voice");

        void store.audio.createAndStartAudioRecording({
          preparedRecording,
          expandVoicePopover: false,
          associatedNoteId: noteId,
          associatedMeetingWindowId: window.id,
          calendarEventId: info?.calendarEventId ?? undefined,
        });
      };

      const onCreateError = () => {
        if (preparedRecording) {
          store.audio.trashRecording({
            audioRecordingId: preparedRecording.audioRecordingId,
            context: { reason: TrashRecordingReason.CreateNoteFailed },
          });
        }
      };

      const calendarEvent = info?.calendarEventId
        ? await store.spaceAccountCalendarEvents.getAsync(info?.calendarEventId)
        : undefined;

      const associatedNote = await (async () => {
        if (!calendarEvent) return;

        const noteId = calendarEvent?.associatedNote?.id;
        if (!noteId) return;

        return store.notes.getAsync(noteId);
      })();

      // If it's changed we don't want to insert the template for calendar event notes.
      if (associatedNote && !associatedNote.isNoteContentInOriginalState) {
        startRecording(associatedNote.id);
        store.navigation.goToNote({ noteId: associatedNote.id });
        return;
      }

      // Create a new note with the detected meeting window as autoStartVoice
      const noteId = associatedNote?.id ?? uuidModule.generate();
      store.notes.composeNewNote({
        eventContext: EventContext.MeetingDetectionNotification,
        calendarEvent,
        noteContent: createNoteEncodedContentForAutoStartVoice({
          windowId: window.id,
          windowTitle: window.title,
          windowUrl: window.url,
          platform: window.platform,
          startTime: info?.startTime ?? undefined,
          endTime: info?.endTime ?? undefined,
          attendees: info?.attendees ?? [],
        }),
        noteId,
        autoFocusPosition: "end",
        onBeforeCreate: async () => {
          if (calendarEvent) {
            const sourceId = resolveSourceIdForCalendarEvent({
              connectedCalendarEventId: calendarEvent!.associatedConnectedCalendarEventId,
            });
            const noteSourceId = resolveNoteSourceSyncModelUuid({
              noteId,
              sourceId,
            });
            // Avoid race condition between ensureSourceForRecording and
            // CreateNoteUsingCalendarEventOperation.
            await new CreateNoteSourceOperation({
              store: store,
              payload: {
                id: noteSourceId,
                schema_version: 1,
                note_id: noteId,
                source_id: sourceId,
              },
            }).execute();
          }
          startRecording(noteId);
        },
        onCreateError,
      });
    },
    [store, clearPendingPrewarm]
  );

  useEffectOnMount(() => {
    if (!store.interface.isDesktop) {
      return;
    }

    if (store.electron.isIntelMacDesktop) {
      /**
       * This feature isn't currently supported on Intel-based MacOS devices.
       */
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const asyncLogic = async () => {
      electronModule.setIsClientReady({ isReady: true });

      try {
        const { shown: advancedMeetingDetectionShown } =
          await electronModule.getAdvancedMeetingDetectionShown();
        advancedMeetingPermissionShownRef.current = advancedMeetingDetectionShown;
      } catch (error) {
        logger.error({
          message: "Failed to get advanced meeting detection shown",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      }

      try {
        unsubscribe = await electronModule.recall.onMeetingEvent?.({
          callback: handleMeetingDetected,
        });
      } catch (error) {
        logger.error({
          message: "Failed to subscribe to meeting detection events",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      }
    };

    void asyncLogic();

    return () => {
      electronModule.setIsClientReady({ isReady: false });
      clearPendingPrewarm();
      try {
        unsubscribe?.();
      } catch (_) {
        /* empty */
      }
    };
  });
};
